import { useQuery } from '@tanstack/react-query';
import {
  ActivityIcon,
  CheckCircle2,
  Clock,
  type LucideIcon,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../api/client.js';
import type { AlarmDto } from '../api/useAlarms.js';
import { useRoster } from '../api/useRoster.js';
import { StatusPill } from '../components/StatusPill.js';
import { cn } from '../lib/cn.js';
import { formatAlarmType, formatRelative } from '../lib/format.js';

// Dashboard home. Single wide fetch (limit=200, no status filter) + client-side derivation
// of the tile counts and the activity feed. One API roundtrip is cheaper than three
// parallel status-filtered requests and simplifies cache invalidation — the WS hub's
// ['alarms'] invalidation covers this query too.

function useAllAlarms() {
  const api = useApi();
  return useQuery({
    queryKey: ['alarms', { limit: 200 }],
    queryFn: () => api<{ alarms: AlarmDto[] }>('/alarms?limit=200'),
    refetchInterval: 15_000,
  });
}

function startOfTodayISO(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export default function Dashboard() {
  const query = useAllAlarms();
  const roster = useRoster();
  const alarms = query.data?.alarms ?? [];
  const nameById = useMemo(
    () => new Map((roster.data?.members ?? []).map((m) => [m.user_id, m.display_name])),
    [roster.data],
  );

  const stats = useMemo(() => {
    const startToday = startOfTodayISO();
    const active = alarms.filter((a) => a.status === 'active').length;
    const acknowledged = alarms.filter((a) => a.status === 'acknowledged').length;
    const triggeredToday = alarms.filter((a) => a.triggered_at >= startToday).length;
    const resolvedToday = alarms.filter(
      (a) =>
        (a.status === 'resolved' || a.status === 'false_alarm') &&
        a.resolved_at != null &&
        a.resolved_at >= startToday,
    ).length;
    const recent = [...alarms]
      .sort((a, b) => b.triggered_at.localeCompare(a.triggered_at))
      .slice(0, 8);
    return { active, acknowledged, triggeredToday, resolvedToday, recent };
  }, [alarms]);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <span className="eyebrow">Operations</span>
        <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
          Overview
        </h1>
        <p className="text-sm text-ink-faint">
          {query.isFetching ? 'Refreshing…' : 'Live feed of alarms across your organisation.'}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          icon={ShieldAlert}
          label="Active alarms"
          value={stats.active}
          tone={stats.active > 0 ? 'danger' : 'neutral'}
          to="/alarms"
          cta="Open"
          emphasis={stats.active > 0}
        />
        <Tile
          icon={Clock}
          label="Acknowledged"
          value={stats.acknowledged}
          tone={stats.acknowledged > 0 ? 'warning' : 'neutral'}
          to="/alarms"
        />
        <Tile
          icon={ActivityIcon}
          label="Triggered today"
          value={stats.triggeredToday}
          tone="info"
        />
        <Tile
          icon={ShieldCheck}
          label="Resolved today"
          value={stats.resolvedToday}
          tone="success"
          to="/incidents"
        />
      </div>

      <div className="panel">
        <div className="panel-head">
          <div>
            <h2 className="font-heading text-sm font-bold text-ink">Recent activity</h2>
            <span className="eyebrow">Last alarms · live</span>
          </div>
          <Link to="/alarms" className="text-xs font-semibold text-brand-purple hover:underline">
            View all alarms →
          </Link>
        </div>
        {query.isError && (
          <div className="mx-6 my-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            Failed to load activity: {(query.error as Error).message}
          </div>
        )}
        {!query.isLoading && stats.recent.length === 0 && (
          <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-ink-faint">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            All quiet — no alarms in recent history.
          </div>
        )}
        {stats.recent.length > 0 && (
          <ul className="divide-y divide-line">
            {stats.recent.map((a) => (
              <li key={a.id}>
                <Link
                  to={`/alarms/${a.id}`}
                  className="flex items-center justify-between gap-4 px-6 py-3 transition hover:bg-surface-3"
                >
                  <div className="flex items-center gap-3">
                    <StatusPill status={a.status} />
                    <div>
                      <div className="text-sm font-medium text-ink">
                        {nameById.get(a.user_id) ?? 'Unknown worker'}
                      </div>
                      <div className="text-xs text-ink-faint">
                        {formatAlarmType(a.type)} · {formatRelative(a.triggered_at)}
                        {a.resolution_note && ` · “${a.resolution_note}”`}
                      </div>
                    </div>
                  </div>
                  {a.status === 'active' && (
                    <span className="shrink-0 text-xs font-semibold text-danger">Awaiting ack</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

type Tone = 'neutral' | 'danger' | 'warning' | 'success' | 'info';

const toneIcon: Record<Tone, string> = {
  neutral: 'text-brand-purple',
  danger: 'text-danger',
  warning: 'text-warning',
  success: 'text-success',
  info: 'text-brand-purple',
};

const toneValue: Record<Tone, string> = {
  neutral: 'text-ink',
  danger: 'text-danger',
  warning: 'text-warning',
  success: 'text-success',
  info: 'text-ink',
};

function Tile({
  icon: Icon,
  label,
  value,
  tone,
  to,
  cta,
  emphasis,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
  tone: Tone;
  to?: string;
  cta?: string;
  emphasis?: boolean;
}) {
  // Match the design's tile shape: icon top-right, monumental tabular numeral on the
  // left, eyebrow label above. Danger tone gets a soft gradient + pulsing border.
  const inner = (
    <div
      className={cn(
        'tile relative',
        emphasis && 'border-danger bg-gradient-to-br from-danger-soft to-surface-1',
      )}
    >
      <Icon
        className={cn('absolute right-4 top-4 h-[18px] w-[18px] opacity-50', toneIcon[tone])}
        strokeWidth={2}
      />
      <span className="eyebrow">{label}</span>
      <div
        className={cn(
          'mt-2 flex items-baseline gap-2 font-heading text-4xl font-extrabold leading-none tracking-tighter',
          toneValue[tone],
        )}
      >
        {value}
      </div>
      {cta && (
        <div className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] font-semibold uppercase tracking-eyebrow-tight text-brand-purple">
          {cta} →
        </div>
      )}
      {emphasis && (
        <div className="pointer-events-none absolute inset-0 rounded-lg border border-danger animate-live-pulse" />
      )}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}
